/** Standard officer roles shown in the admin mock layout. */
export const ADMIN_OFFICER_ROLES = [
  { key: 'president', title: 'President', locked: true },
  { key: 'vice_president', title: 'Vice President', locked: false },
  { key: 'secretary', title: 'Secretary', locked: false },
  { key: 'treasurer', title: 'Treasurer', locked: false },
];

const TITLE_ALIASES = {
  president: ['president'],
  vice_president: ['vice president', 'vice-president', 'vp'],
  secretary: ['secretary'],
  treasurer: ['treasurer'],
};

export function normalizeOfficerTitle(title) {
  return (title || '').trim().toLowerCase();
}

export function officerMatchesRole(officer, roleKey) {
  const normalized = normalizeOfficerTitle(officer?.title);
  if (!normalized) return false;
  const aliases = TITLE_ALIASES[roleKey] || [];
  return aliases.some((alias) => normalized === alias || normalized.includes(alias));
}

export function getMemberDisplayName(member, profile) {
  const fromProfile = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim();
  if (fromProfile) return fromProfile;
  const fromMember = [member?.first_name, member?.last_name].filter(Boolean).join(' ').trim();
  if (fromMember) return fromMember;
  return member?.username || 'Unassigned';
}

export function getInitials(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

/**
 * Build display rows for the four standard officer slots.
 */
export function buildAdminOfficerRows({ club, officers = [], profilesById = {} }) {
  const members = Array.isArray(club?.members) ? club.members : [];
  const presidentMember = club?.president;
  const presidentId = presidentMember?.id ?? club?.president_id;

  return ADMIN_OFFICER_ROLES.map((role) => {
    if (role.key === 'president') {
      const profile = presidentId != null ? profilesById[presidentId] : null;
      const name = getMemberDisplayName(presidentMember, profile);
      const since = club?.established_year ? String(club.established_year) : '—';
      return {
        ...role,
        name,
        initials: getInitials(name),
        since,
        userId: presidentId ?? null,
        officerIndex: null,
      };
    }

    const officerIndex = officers.findIndex((o) => officerMatchesRole(o, role.key));
    const officer = officerIndex >= 0 ? officers[officerIndex] : null;
    let member = officer?.user_id
      ? members.find((m) => String(m.id) === String(officer.user_id))
      : null;
    if (!member && officer?.name) {
      const target = officer.name.trim().toLowerCase();
      member = members.find((m) => {
        const label = getMemberDisplayName(m, profilesById[m.id]).toLowerCase();
        return label === target || (m.username || '').toLowerCase() === target;
      });
    }
    const profile = member?.id != null ? profilesById[member.id] : null;
    const name = officer?.name || (member ? getMemberDisplayName(member, profile) : 'Unassigned');
    const since = club?.established_year ? String(club.established_year) : '—';

    return {
      ...role,
      name,
      initials: getInitials(name),
      since,
      userId: officer?.user_id ?? member?.id ?? null,
      officerIndex: officerIndex >= 0 ? officerIndex : null,
    };
  });
}

export function upsertOfficerForRole(officers, roleTitle, member) {
  const list = Array.isArray(officers) ? [...officers] : [];
  const roleKey = ADMIN_OFFICER_ROLES.find((r) => r.title === roleTitle)?.key;
  const idx = list.findIndex((o) => (roleKey ? officerMatchesRole(o, roleKey) : false));
  const fullName = getMemberDisplayName(member, member?.profile);
  const next = {
    title: roleTitle,
    name: fullName,
    user_id: member?.id ?? null,
  };
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...next };
  } else {
    list.push(next);
  }
  return list;
}

export function formatPlaceholderInviteCode(clubId) {
  const id = Number(clubId);
  if (!Number.isFinite(id)) return 'CLUB-0000';
  return `CLUB-${String(id).padStart(4, '0')}`;
}

export const LISTING_TOGGLE_LABELS = {
  is_listed: 'Public Club Listing',
  is_accepting_members: 'Accepting Members',
  is_promoted: 'Promoted Listing',
  require_approval_to_join: 'Require Approval to Join',
};

export function listingToggleSuccessMessage(field, enabled) {
  const label = LISTING_TOGGLE_LABELS[field] || 'Setting';
  return enabled ? `${label} is now on.` : `${label} is now off.`;
}

export function listingToggleErrorMessage(err) {
  const data = err?.response?.data || {};
  const pick = (...keys) => {
    for (const key of keys) {
      const val = data[key];
      if (Array.isArray(val) && val[0]) return val[0];
      if (typeof val === 'string' && val.trim()) return val;
    }
    return null;
  };
  return (
    pick('is_listed', 'is_promoted', 'is_accepting_members', 'require_approval_to_join', 'error', 'detail')
    || 'We couldn’t update that setting. Please try again.'
  );
}

export function formatInviteExpiry(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

export function inviteCodeExpiryBadge(club) {
  if (!club?.has_active_invite_code) return null;
  const exp = formatInviteExpiry(club.invite_code_expires_at);
  return exp ? `Expires ${exp}` : 'Active';
}

export function inviteCodeFallbackLabel(club) {
  if (club?.has_active_invite_code) {
    return 'Code active — regenerate to view a new code';
  }
  if (club?.invite_code_expires_at) return 'Expired — regenerate a new code';
  return 'No code yet — generate below';
}

export function inviteCodeSuccessMessage(code) {
  if (!code) return 'Your invite code is ready.';
  return `Your new code is ${code}. It’s good for 30 days — copy it now and share it with prospective members.`;
}

export function inviteCodeErrorMessage(err) {
  const data = err?.response?.data || {};
  if (typeof data.error === 'string' && data.error.trim()) return data.error;
  return 'We couldn’t create a new invite code. Please try again.';
}
